import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { shopify, delhivery } from '../services/api';
import {
  Search, ChevronLeft, ChevronRight, Package,
  Clock, CheckCircle, Truck, RotateCcw, X,
  MapPin, ExternalLink, RefreshCw, Loader2, XCircle, Copy
, Trash2 } from 'lucide-react';

const STATUS_CONFIG: Record<string, { label: string; color: string; soft: string; icon: React.ReactNode }> = {
  received: { label: 'Received', color: 'var(--accent-amber)', soft: 'var(--accent-amber-soft)', icon: <Clock size={13} /> },
  fulfilled: { label: 'Fulfilled', color: 'var(--accent-blue)', soft: 'var(--accent-blue-soft)', icon: <Package size={13} /> },
  delivered: { label: 'Delivered', color: 'var(--accent-green)', soft: 'var(--accent-green-soft)', icon: <CheckCircle size={13} /> },
  rto: { label: 'RTO', color: 'var(--accent-red)', soft: 'var(--accent-red-soft)', icon: <RotateCcw size={13} /> },
  cancelled: { label: 'Cancelled', color: 'var(--text-muted)', soft: 'var(--bg-elevated)', icon: <XCircle size={13} /> },
};

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('search') || '';
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteOrder = async (orderId: number) => {
    if (!window.confirm('Permanently delete this order? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await shopify.deleteOrder(orderId);
      setSelectedOrder(null);
      load();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
    setDeleting(false);
  };
  const [tracking, setTracking] = useState<any>(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [liveTracking, setLiveTracking] = useState<any>(null);
  const [liveTrackingLoading, setLiveTrackingLoading] = useState(false);

  const load = () => {
    const params: any = { page, limit: 30 };
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    if (paymentFilter) params.payment_method = paymentFilter;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    shopify.getOrders(params).then(r => {
      setOrders(r.data.orders || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
    }).catch(() => {});
  };

  useEffect(() => { load(); }, [page, statusFilter, paymentFilter, startDate, endDate]);

  // Auto-load on mount if search param exists
  useEffect(() => { if (search) { setPage(1); load(); } }, []);

  const handleSearch = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { setPage(1); load(); }
  };

  const clearFilters = () => {
    setSearch(''); setStatusFilter(''); setPaymentFilter('');
    setStartDate(''); setEndDate(''); setPage(1);
    // Clear URL params
    window.history.replaceState({}, '', '/orders');
  };

  const openOrder = async (order: any) => {
    setSelectedOrder(order);
    setTracking(null);
    setLiveTracking(null);

    // Fetch DB tracking
    if (order.tracking_status) {
      setTrackingLoading(true);
      try {
        const res = await delhivery.track(order.id);
        setTracking(res.data);
      } catch (e) {
        setTracking(null);
      }
      setTrackingLoading(false);

      // Also fetch live tracking from Delhivery API
      setLiveTrackingLoading(true);
      try {
        const res = await delhivery.trackLive(order.tracking_status);
        setLiveTracking(res.data);
      } catch {
        setLiveTracking(null);
      }
      setLiveTrackingLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  const hasFilters = search || statusFilter || paymentFilter || startDate || endDate;

  const inputStyle: React.CSSProperties = {
    padding: '9px 14px', background: 'var(--bg-input)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13,
    outline: 'none', fontFamily: 'inherit',
  };

  const statusSteps = ['received', 'fulfilled', 'delivered'];
  const getStepIndex = (status: string) => {
    if (status === 'rto') return -1;
    return statusSteps.indexOf(status);
  };

  const getStatusColor = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('delivered')) return { bg: 'var(--accent-green-soft)', text: 'var(--accent-green)' };
    if (s.includes('transit') || s.includes('shipped')) return { bg: 'var(--accent-blue-soft)', text: 'var(--accent-blue)' };
    if (s.includes('out for delivery')) return { bg: 'var(--accent-purple-soft)', text: 'var(--accent-purple)' };
    if (s.includes('rto') || s.includes('return') || s.includes('undeliver')) return { bg: 'var(--accent-red-soft)', text: 'var(--accent-red)' };
    if (s.includes('picked')) return { bg: 'var(--accent-amber-soft)', text: 'var(--accent-amber)' };
    return { bg: 'var(--bg-elevated)', text: 'var(--text-secondary)' };
  };

  return (
    <Layout title="Orders" subtitle={`${total} orders found`}>
      {/* Filters */}
      <div className="animate-in" style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', padding: '16px 20px', marginBottom: 16,
      }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '0 14px',
            background: 'var(--bg-input)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)', flex: '1 1 220px', minWidth: 180,
          }}>
            <Search size={14} color="var(--text-muted)" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={handleSearch} placeholder="Search order, customer..."
              style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: 13, outline: 'none', width: '100%', padding: '9px 0' }} />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={inputStyle}>
            <option value="">All Status</option>
            <option value="received">Received</option>
            <option value="fulfilled">Fulfilled</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
            <option value="rto">RTO</option>
          </select>
          <select value={paymentFilter} onChange={e => { setPaymentFilter(e.target.value); setPage(1); }} style={inputStyle}>
            <option value="">All Payments</option>
            <option value="prepaid">Prepaid</option>
            <option value="cod">COD</option>
          </select>
          <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }} style={inputStyle} />
          <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }} style={inputStyle} />
          {hasFilters && (
            <button onClick={clearFilters} style={{
              background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.15)',
              borderRadius: 'var(--radius-sm)', padding: '8px 12px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 5,
              color: 'var(--accent-red)', fontSize: 12, fontWeight: 600,
            }}><X size={13} /> Clear</button>
          )}
        </div>

        {!statusFilter && (
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <button key={key} onClick={() => { setStatusFilter(key); setPage(1); }} style={{
                background: cfg.soft, border: 'none', borderRadius: 20,
                padding: '5px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                color: cfg.color, fontSize: 11.5, fontWeight: 600,
              }}>{cfg.icon} {cfg.label}</button>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="animate-in-1" style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', overflow: 'hidden',
      }}>
        <div className="table-responsive">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Order', 'Customer', 'Date', 'Amount', 'Payment', 'Status', 'AWB / Tracking', 'Province'].map(h => (
                  <th key={h} style={{
                    padding: '11px 14px', textAlign: 'left', fontSize: 10.5,
                    color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((order: any) => {
                const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.received;
                return (
                  <tr key={order.id} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onClick={() => openOrder(order)}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg-card-hover)'}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: 'var(--accent-blue)', fontWeight: 600 }}>{order.order_name}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>{order.customer_name || '—'}</td>
                    <td style={{ padding: '11px 14px', fontSize: 12, color: 'var(--text-muted)' }}>
                      {order.created_at ? new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 }}>₹{(order.total || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{
                        fontSize: 10.5, padding: '3px 8px', borderRadius: 20, fontWeight: 600, textTransform: 'uppercase',
                        background: order.payment_method === 'cod' ? 'var(--accent-amber-soft)' : 'var(--accent-blue-soft)',
                        color: order.payment_method === 'cod' ? 'var(--accent-amber)' : 'var(--accent-blue)',
                      }}>{order.payment_method}</span>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{
                        fontSize: 10.5, padding: '3px 8px', borderRadius: 20, fontWeight: 600,
                        textTransform: 'capitalize', background: cfg.soft, color: cfg.color,
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}>{cfg.icon} {order.status}</span>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      {order.tracking_status ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontSize: 12, color: 'var(--accent-purple)', fontWeight: 600, fontFamily: 'monospace' }}>
                            {order.tracking_status}
                          </span>
                          <button onClick={(e) => { e.stopPropagation(); copyToClipboard(order.tracking_status); }} title="Copy"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 1, display: 'flex' }}>
                            <Copy size={10} color="var(--text-muted)" />
                          </button>
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: 12, color: 'var(--text-muted)' }}>{order.province || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {orders.length === 0 && (
          <div style={{ textAlign: 'center', padding: 50, color: 'var(--text-muted)', fontSize: 13 }}>No orders match your filters.</div>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
            width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.3 : 1,
          }}><ChevronLeft size={16} color="var(--text-secondary)" /></button>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Page {page} of {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
            width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: page === pages ? 'default' : 'pointer', opacity: page === pages ? 0.3 : 1,
          }}><ChevronRight size={16} color="var(--text-secondary)" /></button>
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }} onClick={e => { if (e.target === e.currentTarget) { setSelectedOrder(null); setTracking(null); setLiveTracking(null); } }}>
          <div className="animate-in" style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-xl)', padding: 0, width: 620, maxWidth: '95vw',
            boxShadow: 'var(--shadow-elevated)', maxHeight: '85vh', overflowY: 'auto',
          }}>
            {/* Header */}
            <div style={{
              padding: '24px 28px 18px', borderBottom: '1px solid var(--border)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 2,
              borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0',
            }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>{selectedOrder.order_name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
                  {selectedOrder.customer_name} · {selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleString('en-IN') : ''}
                </div>
              </div>
              <button onClick={() => { setSelectedOrder(null); setTracking(null); setLiveTracking(null); }}
                style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <X size={16} color="var(--text-muted)" />
              </button>
            </div>

            <div style={{ padding: '20px 28px 28px' }}>
              {/* Status Timeline */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 14 }}>Order Lifecycle</div>
                {selectedOrder.status === 'rto' ? (
                  <div style={{
                    background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.15)',
                    borderRadius: 'var(--radius-sm)', padding: '12px 16px',
                    display: 'flex', alignItems: 'center', gap: 10,
                    color: 'var(--accent-red)', fontSize: 13, fontWeight: 600,
                  }}><RotateCcw size={16} /> Return to Origin</div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {statusSteps.map((step, i) => {
                      const currentIdx = getStepIndex(selectedOrder.status);
                      const isComplete = i <= currentIdx;
                      const isCurrent = i === currentIdx;
                      const stepCfg = STATUS_CONFIG[step];
                      return (
                        <React.Fragment key={step}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                            <div style={{
                              width: 34, height: 34, borderRadius: '50%',
                              background: isComplete ? stepCfg.soft : 'var(--bg-elevated)',
                              border: isCurrent ? `2px solid ${stepCfg.color}` : '2px solid transparent',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: isComplete ? stepCfg.color : 'var(--text-muted)',
                            }}>{isComplete ? <CheckCircle size={16} /> : stepCfg.icon}</div>
                            <div style={{
                              fontSize: 10.5, marginTop: 6, fontWeight: isComplete ? 600 : 400,
                              color: isComplete ? stepCfg.color : 'var(--text-muted)', textTransform: 'capitalize',
                            }}>{step}</div>
                          </div>
                          {i < statusSteps.length - 1 && (
                            <div style={{ flex: 0.5, height: 2, borderRadius: 1, marginBottom: 20, background: i < currentIdx ? 'var(--accent-green)' : 'var(--bg-elevated)' }} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Live Tracking Section */}
              {selectedOrder.tracking_status && (
                <div style={{
                  background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)',
                  padding: '18px 20px', marginBottom: 24, border: '1px solid var(--border)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                    <Truck size={15} color="var(--accent-purple)" />
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Shipment Tracking</span>
                  </div>

                  {/* AWB + Copy */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>AWB:</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent-purple)', fontFamily: 'monospace' }}>
                      {selectedOrder.tracking_status}
                    </span>
                    <button onClick={() => copyToClipboard(selectedOrder.tracking_status)} title="Copy AWB"
                      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', display: 'flex' }}>
                      <Copy size={11} color="var(--text-muted)" />
                    </button>
                  </div>

                  {liveTrackingLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 0', gap: 8, color: 'var(--text-muted)', fontSize: 13 }}>
                      <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Fetching live tracking...
                    </div>
                  ) : liveTracking && liveTracking.current ? (
                    <>
                      {/* Status + Courier + EDD */}
                      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
                        {liveTracking.current.current_status && (() => {
                          const sc = getStatusColor(liveTracking.current.current_status);
                          return (
                            <div>
                              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3 }}>STATUS</div>
                              <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: sc.bg, color: sc.text }}>
                                {liveTracking.current.current_status}
                              </span>
                            </div>
                          );
                        })()}
                        {liveTracking.current.courier_name && (
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3 }}>COURIER</div>
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{liveTracking.current.courier_name}</span>
                          </div>
                        )}
                        {liveTracking.current.edd && (
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3 }}>EST. DELIVERY</div>
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-amber)' }}>{liveTracking.current.edd}</span>
                          </div>
                        )}
                      </div>

                      {/* Activity Timeline */}
                      {liveTracking.activities && liveTracking.activities.length > 0 && (
                        <div style={{ maxHeight: 180, overflowY: 'auto', paddingRight: 4 }}>
                          {liveTracking.activities.slice(0, 6).map((act: any, i: number) => (
                            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                                <div style={{
                                  width: 7, height: 7, borderRadius: '50%',
                                  background: i === 0 ? 'var(--accent-green)' : 'var(--border-light)',
                                }} />
                                {i < Math.min(liveTracking.activities.length, 6) - 1 && (
                                  <div style={{ width: 1, flex: 1, background: 'var(--border)', minHeight: 14 }} />
                                )}
                              </div>
                              <div style={{ flex: 1, paddingBottom: 2 }}>
                                <div style={{ fontSize: 11.5, fontWeight: 500, color: i === 0 ? 'var(--accent-green)' : 'var(--text-primary)' }}>{act.activity || act.status}</div>
                                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>
                                  {act.location && <>{act.location} · </>}{act.date}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                      <div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3 }}>AWB</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-purple)' }}>{selectedOrder.tracking_status}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3 }}>Status</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{tracking?.tracking_status || selectedOrder.status}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3 }}>Updated</div>
                        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                          {tracking?.tracking_updated_at ? new Date(tracking.tracking_updated_at).toLocaleString('en-IN') : '—'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Line Items */}
              {selectedOrder.line_items && selectedOrder.line_items.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 10 }}>Products</div>
                  {selectedOrder.line_items.map((li: any, i: number) => (
                    <div key={i} style={{
                      background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)',
                      padding: '10px 14px', marginBottom: 6, border: '1px solid var(--border)',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{li.product_title}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                          Size: <span style={{ color: 'var(--accent-blue)', fontWeight: 600 }}>{li.variant_title || '—'}</span> · Qty: {li.quantity}
                        </div>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>₹{(li.price || 0).toLocaleString('en-IN')}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Order Details Grid */}
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 12 }}>Order Details</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  { label: 'Customer', value: selectedOrder.customer_name || '—' },
                  { label: 'Province', value: selectedOrder.province || '—' },
                  { label: 'Total', value: `₹${(selectedOrder.total || 0).toLocaleString('en-IN')}` },
                  { label: 'Subtotal', value: `₹${(selectedOrder.subtotal || 0).toLocaleString('en-IN')}` },
                  { label: 'Payment', value: (selectedOrder.payment_method || '').toUpperCase() },
                  { label: 'Financial Status', value: selectedOrder.financial_status || '—' },
                  { label: 'Quantity', value: selectedOrder.quantity },
                  { label: 'COD Charges', value: `₹${selectedOrder.cod_charges || 0}` },
                ].map((d, i) => (
                  <div key={i} style={{
                    background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: '10px 14px',
                  }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 3, fontWeight: 600 }}>{d.label}</div>
                    <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{d.value}</div>
                  </div>
                ))}
              </div>

              {/* Delete Order */}
              <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                <button onClick={() => handleDeleteOrder(selectedOrder.id)} disabled={deleting} style={{
                  width: '100%', padding: '12px', background: 'var(--accent-red-soft)',
                  border: '1px solid rgba(239,90,111,0.2)', borderRadius: 'var(--radius-sm)',
                  color: 'var(--accent-red)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  opacity: deleting ? 0.7 : 1,
                }}>
                  <Trash2 size={14} /> {deleting ? 'Deleting...' : 'Delete Order Permanently'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Layout>
  );
};

export default Orders;
